import {Field, FormRenderProps} from '@progress/kendo-react-form';
import {Geometry, Point} from 'geojson';
import {FunctionComponent, useCallback, useEffect, useMemo, useState} from 'react';

import styles from './AddressDetails.module.scss';
import stylesMap from './AddressMap.module.scss';
import {AddressMap} from './AddressMap';
import {IGeocodingAddress} from "../../../../model";
import {FormInput, FormTimeZoneDropdown, GoogleAddressAutoCompleteInput, TextFormField} from "../../../common";

interface IProps extends FormRenderProps {
    edit?: boolean;
    geometry?: Geometry | undefined | null;
}

export const AddressDetails: FunctionComponent<IProps> = (formRenderProps: IProps) => {
    const {edit, geometry} = formRenderProps;
    const [position, setPosition] = useState<[number, number]>();
    const [zoom, setZoom] = useState<number>();

    useEffect(() => {
        if (geometry) {
            const point = geometry as Point;
            setPosition(point.coordinates as [number, number]);
            setZoom(16);
        }
    }, [geometry]);

    const posChangeHandler = useCallback(
        (event: { position: [number, number]; address: IGeocodingAddress }) => {
            setPosition(event.position);
            setZoom(16);

            formRenderProps.onChange('address.geometry', {
                value: {
                    type: 'Point',
                    coordinates: event.position as number[]
                } as Point
            });
            formRenderProps.onChange('address.suite', {value: ''});
            formRenderProps.onChange('address.country', {value: event.address.country ?? ''});
            formRenderProps.onChange('address.state', {value: event.address.state ?? ''});
            formRenderProps.onChange('address.city', {value: event.address.city ?? ''});
            formRenderProps.onChange('address.street', {value: event.address.street ?? ''});
            formRenderProps.onChange('address.zipCode', {value: event.address.zip ?? ''});
        },
        [formRenderProps],
    );

    const content = useMemo(
        () => (
            <div className={styles.panel}>
                <div className={styles.leftPanel}>
                    <Field
                        name={'address.street'}
                        label={'Street'}
                        layer={'street'}
                        component={edit ? GoogleAddressAutoCompleteInput : TextFormField}
                        readOnly={true}
                        required={false}
                        onPlaceSelected={posChangeHandler}
                    />
                    <Field name={'address.suite'} label={'Suite'} required={false}
                           component={edit ? FormInput : TextFormField}/>
                    <Field name={'address.city'} label={'City'} layer={'city'} required={false}
                           component={TextFormField} readOnly={true}/>
                    <Field name={'address.state'} label={'State'} layer={'state'} required={false}
                           component={TextFormField} readOnly={true}/>
                    <Field name={'address.zipCode'} label={'Zip Code'} component={TextFormField}
                           readOnly={true} required={false} />
                    <Field
                        name={'address.country'}
                        label={'Country'}
                        layer={'country'}
                        component={TextFormField}
                        readOnly={true}
                        required={false}
                    />
                    <FormTimeZoneDropdown name='address.timezone' label='Time Zone' edit={edit || false}
                                          required={false}/>
                </div>
                <div className={styles.rightPanel}>
                    <AddressMap className={stylesMap.map} edit={edit} zoom={zoom} position={position}/>
                </div>
            </div>
        ),
        [edit, posChangeHandler, position, zoom],
    );

    return content;
};
